import { Component } from '@theme/component';
import { getBundleStore } from '@theme/pdp-bundle-store';
import { formatMoney } from '@theme/money-formatting';
import { prefersReducedMotion } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { getCartManager } from '@theme/cart-manager';

const SLIDE_SIZES = '(min-width: 750px) 34rem, 92vw';
const ZOOMED_SIZES = '(min-width: 750px) 102rem, 276vw';
const COMBO_WIDTHS = [600, 900, 1200, 1600];
const BAND_WIDTHS = [400, 800, 1200, 1600];
const PICK_DELAY = 220;
const ADD_DURATION = 800;
const CART_IMAGE_WIDTH = 120;

/**
 * @typedef {Object} BandsRefs
 * @property {HTMLElement} status - Live region
 * @property {HTMLButtonElement[]} swatches - Band swatches (layout A) or cards (layout B)
 * @property {HTMLElement} boxSummary - "In your box" summary
 * @property {HTMLUListElement} boxList - "In your box" list
 * @property {HTMLTemplateElement} boxTemplate - Row template for an added band
 */

/**
 * @typedef {Object} Band
 * @property {string} id - Variant id
 * @property {string} title
 * @property {string} color - Color name (`color_swatch` label)
 * @property {string} material - Material key
 * @property {string} materialLabel
 * @property {string} price - Formatted price
 * @property {number} priceCents
 * @property {string} image - Small image (box list, cart drawer)
 * @property {string} bandSrc - Large band image
 * @property {string} composite - `{watch_sku}__{band_sku}.png` combo image in Shopify Files (may not exist)
 */

/**
 * @typedef {Object} CartBand
 * @property {string} title
 * @property {string} image
 * @property {number} quantity
 * @property {number} original - Line price before discounts, in cents
 * @property {number} final - Line price after the discounts computed by Shopify, in cents
 * @property {string[]} discounts - Titles of the discounts applied to the line
 */

/**
 * @param {string} url
 * @param {number} width
 * @returns {string} URL resized by the Shopify CDN
 */
const sized = (url, width) => {
  const resized = new URL(url, window.location.href);
  resized.searchParams.set('width', String(width));
  return resized.toString();
};

/**
 * @param {string} url
 * @param {number[]} widths
 * @returns {string}
 */
const srcset = (url, widths) => widths.map((width) => `${sized(url, width)} ${width}w`).join(', ');

/**
 * Buy Box V2 extra bands: layout A (swatches opening a band popin per material) or B (tabs, card
 * carousel), plus the shared "In your box" recap.
 * Added bands join the add-to-cart batch through the PDP bundle store (contributor `bands`).
 * With the BOGOF offer on, a ticket and a popin reminder count the added bands plus the bands
 * already in the cart, by pairs (display only, the discount is applied in the cart).
 *
 * @extends {Component<BandsRefs>}
 */
class BuyBoxV2Bands extends Component {
  requiredRefs = ['boxList', 'boxTemplate'];

  /** @type {string | null} Band shown in the popin, null while it is closed */
  #focusedId = null;

  /** @type {string[]} */
  #addedIds = [];

  /** @type {(() => void) | null} */
  #unregister = null;

  /** @type {string | null} Material whose slides are in the popin */
  #viewerMaterial = null;

  /** @type {'combo' | 'band'} */
  #view = 'combo';

  /** @type {HTMLElement | null} */
  #opener = null;

  /** @type {Set<string>} */
  #warmed = new Set();

  /** @type {string | null} Band whose add is playing its progress in the popin */
  #addingId = null;

  /** @type {number | undefined} */
  #addingTimeout;

  /** @type {string | null} Band whose added check is drawn in the popin button */
  #justAddedId = null;

  /** @type {CartBand[]} Band lines already in the cart */
  #cartBands = [];

  connectedCallback() {
    super.connectedCallback();
    this.#readCartBands();
    this.#register();
    this.#render();
    this.addEventListener('close', this.#onViewerClose, true);
    this.addEventListener('pointerover', this.#warm, { passive: true });
    this.addEventListener('focusin', this.#warm);
    document.addEventListener('model:update', this.#onModelUpdate);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unregister?.();
    this.#unregister = null;
    this.removeEventListener('close', this.#onViewerClose, true);
    this.removeEventListener('pointerover', this.#warm);
    this.removeEventListener('focusin', this.#warm);
    document.removeEventListener('model:update', this.#onModelUpdate);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    clearTimeout(this.#addingTimeout);
  }

  /**
   * Opens the band popin on a swatch's band, or on an added band from its "In your box" image (layout A).
   * @param {Event} event
   */
  openBand(event) {
    const swatch = event.target;
    if (!(swatch instanceof HTMLButtonElement) || swatch.disabled) return;
    const row = swatch.closest('[data-band-id]');
    const band = this.#band(row instanceof HTMLElement ? row.dataset.bandId ?? '' : '');
    if (!band || !this.#isAvailable(band.id)) return;

    if (this.#focusedId) return;
    this.#focusedId = band.id;
    this.#opener = swatch;
    swatch.setAttribute('aria-expanded', 'true');
    this.#buildViewer(band.material);
    this.#renderViewer();

    /** @type {(HTMLElement & { showDialog?: () => void }) | null} */
    const viewer = this.querySelector('dialog-component');
    window.setTimeout(
      () => {
        if (this.#focusedId !== band.id) return;
        viewer?.showDialog?.();
        this.#startSwiper(this.#viewerIds.indexOf(band.id));
      },
      prefersReducedMotion() ? 0 : PICK_DELAY
    );
  }

  /**
   * Shows a band of the popin from its color button.
   * @param {number} index
   */
  showBand(index) {
    this.#swiper?.slideTo(Number(index));
  }

  /** Shows the previous band in the popin. */
  showPrevious() {
    this.#swiper?.slidePrev();
  }

  /** Shows the next band in the popin. */
  showNext() {
    this.#swiper?.slideNext();
  }

  /**
   * Switches the popin between the watch + band combo and the band alone.
   * @param {Event} event
   */
  setView(event) {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    this.#view = button.dataset.viewerView === 'band' ? 'band' : 'combo';
    this.#renderViewer();
  }

  /**
   * Adds or removes the band of a carousel card (layout B).
   * @param {Event} event
   */
  toggleBand(event) {
    const card = event.target;
    if (!(card instanceof HTMLButtonElement) || card.disabled) return;
    this.#toggle(card.dataset.bandId ?? '');
  }

  /**
   * Shows the carousel of one material (layout B tabs).
   * @param {Event} event
   */
  selectMaterial(event) {
    const tab = event.target;
    if (!(tab instanceof HTMLButtonElement)) return;

    const material = tab.dataset.material ?? '';
    for (const element of this.querySelectorAll('.buy-box-v2-bands__tab')) {
      element.setAttribute('aria-pressed', String(element === tab));
    }
    for (const rail of this.querySelectorAll('.buy-box-v2-bands__rail')) {
      if (!(rail instanceof HTMLElement)) continue;
      rail.hidden = rail.dataset.material !== material;
      if (!rail.hidden) rail.scrollLeft = 0;
    }
  }

  /**
   * Adds or removes the band shown in the popin. Adding from the main button plays a short progress
   * before the band is committed, removing is immediate.
   * @param {Event} [event]
   */
  toggleFocused(event) {
    const id = this.#focusedId;
    if (!id || !this.#isAvailable(id) || this.#addingId) return;

    const button = event?.target instanceof Element ? event.target.closest('[data-viewer-add]') : null;
    if (!(button instanceof HTMLElement) || this.#addedIds.includes(id) || prefersReducedMotion()) {
      this.#toggle(id);
      return;
    }

    this.#addingId = id;
    button.style.setProperty('--bbv2-add-duration', `${ADD_DURATION}ms`);
    button.toggleAttribute('data-adding', true);
    button.setAttribute('aria-busy', 'true');
    this.#addingTimeout = setTimeout(() => this.#finishAdding(), ADD_DURATION);
  }

  /** Commits the band whose add progress is playing and draws the added check. */
  #finishAdding() {
    clearTimeout(this.#addingTimeout);
    const id = this.#addingId;
    this.#addingId = null;
    const button = this.querySelector('[data-viewer-add]');
    button?.removeAttribute('data-adding');
    button?.removeAttribute('aria-busy');
    if (!id) return;

    this.#justAddedId = id;
    button?.toggleAttribute('data-just-added', true);
    if (!this.#addedIds.includes(id)) this.#toggle(id);
  }

  /**
   * Removes a band from the "In your box" list.
   * @param {Event} event
   */
  removeBand(event) {
    const row = event.target instanceof Element ? event.target.closest('[data-band-id]') : null;
    if (!(row instanceof HTMLElement)) return;

    this.#toggle(row.dataset.bandId ?? '');
    const summary = this.querySelector('.buy-box-v2-bands__box-toggle');
    if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
  }

  /** @param {string} id */
  #toggle(id) {
    const added = this.#addedIds.includes(id);
    this.#addedIds = added ? this.#addedIds.filter((value) => value !== id) : [...this.#addedIds, id];
    this.#announce(this.#band(id)?.title ?? '', !added);
    this.#render();
    getBundleStore().publish('bands');
  }

  /**
   * @param {string} price - Formatted price
   * @returns {string} Price as an extra cost (ex. "+$35.95")
   */
  #extra(price) {
    return (this.dataset.extra ?? '[price]').replace('[price]', price);
  }

  /**
   * @param {string} id
   * @returns {boolean}
   */
  #isAvailable(id) {
    const swatch = this.#swatches.find((element) => element.dataset.bandId === id);
    return Boolean(swatch && !swatch.disabled);
  }

  #register() {
    const store = getBundleStore();
    store.setContext({ productId: this.dataset.productId ?? '', sectionId: this.dataset.sectionId ?? '' });
    this.#unregister = store.register({
      name: 'bands',
      getSectionId: () => this.dataset.sectionId ?? '',
      order: 10,
      getItems: (ctx) =>
        this.#addedBands().map((band) => ({
          id: Number(band.id),
          quantity: 1,
          properties: { _bundle_role: 'band', _bundle_parent_variant: ctx.mainVariantId },
        })),
      getDisplay: () =>
        this.#addedBands().map((band) => ({
          role: 'band',
          title: band.title,
          priceHtml: band.price,
          priceCents: band.priceCents,
          imageUrl: band.image,
          variantId: band.id,
        })),
      onCommit: () => {
        this.#addedIds = [];
        this.#render();
        getBundleStore().publish('bands');
      },
    });
  }

  #onModelUpdate = () => {
    getBundleStore().setContext({ productId: this.dataset.productId ?? '', sectionId: this.dataset.sectionId ?? '' });
    this.#focusedId = null;
    this.#addedIds = [];
    this.#viewerMaterial = null;
    this.#opener = null;
    this.#warmed.clear();
    this.#readCartBands();
    this.#render();
    getBundleStore().publish('bands');
  };

  /** Clears the popin selection once it is closed: only added bands stay marked. */
  #onViewerClose = (/** @type {Event} */ event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.matches('[data-viewer]')) return;

    if (this.#addingId) this.#finishAdding();

    this.#swiper?.zoom?.out?.();
    this.#focusedId = null;
    this.#opener?.setAttribute('aria-expanded', 'false');
    const focusTarget = this.#opener?.isConnected ? this.#opener : this.querySelector('.buy-box-v2-bands__box-toggle');
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    this.#opener = null;
  };

  /**
   * Preloads the popin images of a hovered or focused swatch.
   * @param {Event} event
   */
  #warm = (event) => {
    const swatch = event.target instanceof Element ? event.target.closest('.buy-box-v2-bands__swatch') : null;
    if (!(swatch instanceof HTMLElement) || this.#warmed.has(swatch.dataset.bandId ?? '')) return;
    const band = this.#band(swatch.dataset.bandId ?? '');
    if (!band) return;

    this.#warmed.add(band.id);
    for (const [url, widths] of /** @type {Array<[string, number[]]>} */ ([
      [band.composite, COMBO_WIDTHS],
      [band.bandSrc, BAND_WIDTHS],
    ])) {
      if (!url) continue;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.setAttribute('imagesrcset', srcset(url, widths));
      link.setAttribute('imagesizes', SLIDE_SIZES);
      document.head.append(link);
    }
  };

  /** @returns {HTMLButtonElement[]} */
  get #swatches() {
    return Array.from(this.querySelectorAll('button[data-band-id]'));
  }

  /**
   * @param {string} id
   * @returns {Band | null}
   */
  #band(id) {
    const swatch = this.#swatches.find((element) => element.dataset.bandId === id);
    if (!swatch) return null;

    return {
      id,
      title: swatch.dataset.title ?? '',
      color: swatch.dataset.color ?? swatch.dataset.title ?? '',
      material: swatch.dataset.material ?? '',
      materialLabel: swatch.dataset.materialLabel ?? '',
      price: swatch.dataset.price ?? '',
      priceCents: Number(swatch.dataset.priceCents ?? 0),
      image: swatch.dataset.image ?? '',
      bandSrc: swatch.dataset.bandSrc ?? '',
      composite: swatch.dataset.composite ?? '',
    };
  }

  /** @returns {Band[]} */
  #addedBands() {
    return this.#addedIds.map((id) => this.#band(id)).filter((band) => band !== null);
  }

  #render() {
    for (const button of this.#swatches) {
      const id = button.dataset.bandId ?? '';
      const added = this.#addedIds.includes(id);
      if (button.classList.contains('buy-box-v2-bands__card')) button.setAttribute('aria-pressed', String(added));
      button.toggleAttribute('data-added', added);
      const addedLabel = button.querySelector('[data-swatch-added]');
      if (addedLabel instanceof HTMLElement) addedLabel.hidden = !added;
    }

    this.#renderBox();
    this.#renderOffer();
    if (this.#focusedId) this.#renderViewer();
  }

  /** @returns {HTMLElement | null} BOGOF ticket, holding the offer texts */
  get #offer() {
    return this.querySelector('[data-bogof]');
  }

  /** Reads the cart bands rendered by Liquid. */
  #readCartBands() {
    const data = this.querySelector('script[data-bogof-cart]');
    try {
      const lines = JSON.parse(data?.textContent ?? '[]');
      this.#cartBands = Array.isArray(lines) ? lines : [];
    } catch {
      this.#cartBands = [];
    }
  }

  /** Recounts the bands of the cart (`wristbands` products) once it changes. */
  #onCartUpdate = () => {
    const offer = this.#offer;
    const items = getCartManager().cartJSON?.items;
    if (!offer || !Array.isArray(items)) return;

    const productIds = offer.dataset.productIds ?? '';
    this.#cartBands = items
      .filter((item) => productIds.includes(`,${item.product_id},`))
      .map((item) => ({
        title: item.product_title ?? '',
        image: item.image ? sized(item.image, CART_IMAGE_WIDTH) : '',
        quantity: Number(item.quantity ?? 0),
        original: Number(item.original_line_price ?? 0),
        final: Number(item.final_line_price ?? 0),
        discounts: (item.line_level_discount_allocations ?? [])
          .map((/** @type {any} */ allocation) => allocation.discount_application?.title ?? '')
          .filter(Boolean),
      }));
    this.#renderCartBox();
    this.#renderOffer();
  };

  /** Lists the bands already in the cart in "In your box", with the prices computed by Shopify. */
  #renderCartBox() {
    const list = this.querySelector('[ref="boxList"]');
    const template = this.querySelector('template[data-cart-template]');
    const offer = this.#offer;
    if (!(list instanceof HTMLElement) || !(template instanceof HTMLTemplateElement) || !offer) return;

    for (const row of list.querySelectorAll('[data-cart-band]')) {
      row.remove();
    }
    if (this.#cartBands.length === 0) return;

    const group = document.createElement('li');
    group.className = 'buy-box-v2-bands__box-group';
    group.dataset.cartBand = '';
    group.textContent = offer.dataset.inCartLabel ?? '';
    list.append(group);

    const money = (/** @type {number} */ cents) =>
      formatMoney(cents, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? 'USD');

    for (const line of this.#cartBands) {
      const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const row = fragment.querySelector('li');
      if (!row) continue;

      const image = row.querySelector('img');
      if (image && line.image) image.src = line.image;
      else image?.remove();

      const name = row.querySelector('.buy-box-v2-bands__box-name');
      if (name) {
        name.textContent =
          line.quantity > 1
            ? (offer.dataset.quantityLabel ?? '[count] × [title]')
                .replace('[count]', String(line.quantity))
                .replace('[title]', line.title)
            : line.title;
      }
      const discount = row.querySelector('.buy-box-v2-bands__box-discount');
      if (discount) discount.textContent = line.discounts.join(' · ');

      const price = row.querySelector('.buy-box-v2-bands__box-price');
      if (price) {
        const final = document.createElement('span');
        if (line.final === 0) {
          final.dataset.free = '';
          final.textContent = offer.dataset.freeLabel ?? '';
        } else {
          final.textContent = money(line.final);
        }
        if (line.final < line.original) {
          const original = document.createElement('s');
          original.textContent = money(line.original);
          price.append(original);
        }
        price.append(final);
      }
      list.append(fragment);
    }
  }

  /** Updates the BOGOF ticket and popin reminder from the bands counted by pairs. */
  #renderOffer() {
    const offer = this.#offer;
    if (!offer?.hasAttribute('data-gauge')) return;

    const cartImages = this.#cartBands.flatMap((line) => Array.from({ length: line.quantity }, () => line.image));
    const images = [...cartImages, ...this.#addedBands().map((band) => band.image)];
    const count = images.length;
    const free = Math.floor(count / 2);
    const paired = count > 0 && count % 2 === 0;
    const state = count === 0 ? 'start' : paired ? 'unlocked' : 'progress';
    const text = offer.dataset;
    const freeLabel = ((free === 1 ? text.unlockedOne : text.unlockedOther) ?? '').replace('[count]', String(free));

    let eyebrow = text.eyebrow ?? '';
    let title = text.title ?? '';
    let hint = text.text ?? '';
    let reminder = text.title ?? '';
    if (state === 'progress') {
      eyebrow = free === 0 ? text.progressEyebrow ?? '' : freeLabel;
      title = (free === 0 ? text.progressTitle : text.nextTitle) ?? '';
      hint = text.progressText ?? '';
      reminder = title;
    } else if (state === 'unlocked') {
      eyebrow = text.unlockedEyebrow ?? '';
      title = freeLabel;
      hint = text.unlockedText ?? '';
      reminder = eyebrow;
    }

    offer.dataset.state = state;
    this.#setText('[data-bogof-eyebrow]', eyebrow);
    this.#setText('[data-bogof-title]', title);
    this.#setText('[data-bogof-text]', hint);

    const filled = paired ? 2 : count % 2;
    for (const [index, bar] of this.querySelectorAll('[data-bogof-bar]').entries()) {
      bar.toggleAttribute('data-filled', index % 2 < filled);
    }

    const popin = this.querySelector('[data-bogof-popin]');
    if (!(popin instanceof HTMLElement)) return;
    popin.dataset.state = state;
    this.#setText('[data-bogof-popin-message]', reminder);
    const pill = popin.querySelector('[data-bogof-popin-pill]');
    if (pill instanceof HTMLElement) {
      pill.hidden = free === 0;
      if (pill.textContent !== freeLabel) pill.textContent = freeLabel;
    }

    const pairStart = paired ? count - 2 : count - (count % 2);
    for (const [index, slot] of popin.querySelectorAll('[data-bogof-slot]').entries()) {
      const position = pairStart + index;
      const image = images[position] ?? '';
      slot.toggleAttribute('data-filled', position < count);
      const current = slot.querySelector('img');
      if (!image) {
        current?.remove();
        continue;
      }
      if (current?.getAttribute('src') === image) continue;
      const element = current ?? document.createElement('img');
      element.alt = '';
      element.src = image;
      if (!current) slot.append(element);
    }
  }

  #renderBox() {
    const list = this.querySelector('[ref="boxList"]');
    const template = this.querySelector('template[ref="boxTemplate"]');
    if (!(list instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

    for (const row of list.querySelectorAll('li[data-band-id]')) {
      row.remove();
    }

    const bands = this.#addedBands();
    for (const band of bands) {
      const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const row = fragment.querySelector('li');
      if (!row) continue;

      row.dataset.bandId = band.id;
      const image = row.querySelector('img');
      if (image && band.image) image.src = band.image;
      else image?.remove();
      const name = row.querySelector('.buy-box-v2-bands__box-name');
      if (name) name.textContent = band.title;
      const price = row.querySelector('.buy-box-v2-bands__box-price');
      if (price) price.textContent = this.#extra(band.price);
      row
        .querySelector('.buy-box-v2-bands__box-remove')
        ?.setAttribute('aria-label', (this.dataset.removeLabel ?? '').replace('[title]', band.title));
      row
        .querySelector('.buy-box-v2-bands__box-media')
        ?.setAttribute('aria-label', (this.dataset.viewLabel ?? '').replace('[title]', band.title));
      list.append(fragment);
    }
    this.#renderCartBox();

    const summary = this.querySelector('[ref="boxSummary"]');
    if (!summary) return;

    const count = bands.length + Number(this.dataset.includedCount ?? 0);
    const countLabel = (count === 1 ? this.dataset.countOne : this.dataset.countOther) ?? '';
    const parts = [countLabel.replace('[count]', String(count))];
    const totalCents = bands.reduce((total, band) => total + band.priceCents, 0);
    if (totalCents > 0) {
      const money = formatMoney(totalCents, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? 'USD');
      parts.push((this.dataset.extra ?? '').replace('[price]', money));
    }
    summary.textContent = count > 0 ? parts.join(' · ') : '';
  }

  /** @returns {HTMLElement | null} */
  get #viewer() {
    return this.querySelector('[data-viewer]');
  }

  /** @returns {(HTMLElement & { swiper?: any, initialize?: () => void }) | null} */
  get #swiperElement() {
    return this.querySelector('[data-viewer-swiper]');
  }

  /** @returns {any} Swiper instance of the popin */
  get #swiper() {
    return this.#swiperElement?.swiper ?? null;
  }

  /** @returns {string[]} Band ids of the popin slides, in order */
  get #viewerIds() {
    return Array.from(this.#swiperElement?.children ?? []).map((slide) =>
      slide instanceof HTMLElement ? slide.dataset.bandId ?? '' : ''
    );
  }

  /**
   * Fills the popin with the bands of one material: slides and color buttons.
   * @param {string} material
   */
  #buildViewer(material) {
    const swiperElement = this.#swiperElement;
    const viewer = this.#viewer;
    if (!swiperElement || !viewer || (material === this.#viewerMaterial && swiperElement.childElementCount)) return;

    const bands = this.#swatches
      .filter((swatch) => swatch.dataset.material === material && !swatch.disabled)
      .map((swatch) => this.#band(swatch.dataset.bandId ?? ''))
      .filter((band) => band !== null);

    swiperElement.replaceChildren(...bands.map((band) => this.#slide(band)));
    this.querySelector('[data-viewer-colors]')?.replaceChildren(
      ...bands.map((band, index) => this.#colorButton(band, index))
    );
    viewer.toggleAttribute('data-single', bands.length < 2);
    this.#viewerMaterial = material;
  }

  /**
   * @param {Band} band
   * @returns {HTMLElement} Zoomable slide with the combo image (when it exists) over the band image
   */
  #slide(band) {
    const slide = document.createElement('swiper-slide');
    slide.className = 'buy-box-v2-bands__slide';
    slide.dataset.bandId = band.id;

    const frame = document.createElement('div');
    frame.className = 'swiper-zoom-target buy-box-v2-bands__slide-frame';
    if (band.bandSrc) frame.append(this.#image(band.bandSrc, BAND_WIDTHS, 'buy-box-v2-bands__slide-band'));
    if (band.composite) {
      slide.toggleAttribute('data-has-combo', true);
      const combo = this.#image(band.composite, COMBO_WIDTHS, 'buy-box-v2-bands__slide-combo');
      combo.addEventListener(
        'error',
        () => {
          combo.remove();
          slide.removeAttribute('data-has-combo');
          if (this.#focusedId === band.id) this.#renderViewer();
        },
        { once: true }
      );
      frame.append(combo);
    }

    const container = document.createElement('div');
    container.className = 'swiper-zoom-container';
    container.append(frame);
    slide.append(container);
    return slide;
  }

  /**
   * @param {string} url
   * @param {number[]} widths
   * @param {string} className
   * @returns {HTMLImageElement}
   */
  #image(url, widths, className) {
    const image = document.createElement('img');
    image.className = className;
    image.alt = '';
    image.decoding = 'async';
    image.draggable = false;
    image.sizes = SLIDE_SIZES;
    image.srcset = srcset(url, widths);
    image.src = sized(url, widths[1] ?? widths[0]);
    image.addEventListener('load', () => image.toggleAttribute('data-loaded', true), { once: true });
    return image;
  }

  /**
   * @param {Band} band
   * @param {number} index
   * @returns {HTMLButtonElement} Color button of the popin
   */
  #colorButton(band, index) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'buy-box-v2-bands__swatch buy-box-v2-bands__viewer-swatch';
    button.dataset.index = String(index);
    button.dataset.viewerBand = band.id;
    button.setAttribute('aria-label', band.color);
    button.setAttribute('on:click', `buy-box-v2-bands/showBand/${index}`);

    const media = document.createElement('span');
    media.className = 'buy-box-v2-bands__swatch-media';
    if (band.image) {
      const image = document.createElement('img');
      image.className = 'buy-box-v2-bands__swatch-image';
      image.src = band.image;
      image.alt = '';
      media.append(image);
    }
    const check = this.querySelector('.buy-box-v2-bands__swatch-check')?.cloneNode(true);
    button.append(media);
    if (check) button.append(check);
    return button;
  }

  /** @param {number} index - Slide to show once the popin is open */
  #startSwiper(index) {
    const swiperElement = this.#swiperElement;
    if (!swiperElement) return;

    if (!swiperElement.swiper && typeof swiperElement.initialize === 'function') {
      Object.assign(swiperElement, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 350,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        allowTouchMove: false,
        observer: true,
        keyboard: { enabled: true },
        zoom: { maxRatio: 3, toggle: true },
        a11y: {
          enabled: true,
          prevSlideMessage: this.dataset.previousLabel ?? '',
          nextSlideMessage: this.dataset.nextLabel ?? '',
        },
      });
      swiperElement.initialize();
      swiperElement.swiper?.on('slideChange', this.#onSlideChange);
      swiperElement.swiper?.on('zoomChange', this.#onZoomChange);
    }

    requestAnimationFrame(() => {
      const swiper = this.#swiper;
      if (!swiper) return;
      swiper.update();
      swiper.slideTo(Math.max(0, index), 0);
      this.#renderViewer();
    });
  }

  #onSlideChange = () => {
    const id = this.#viewerIds[this.#swiper?.activeIndex ?? 0];
    if (!id || !this.#focusedId || id === this.#focusedId) return;
    this.#focusedId = id;
    this.#renderViewer();
  };

  /** Requests sharper images once the slide is zoomed in. */
  #onZoomChange = (/** @type {any} */ _swiper, /** @type {number} */ scale) => {
    if (scale <= 1) return;
    const slide = this.#swiper?.slides?.[this.#swiper.activeIndex];
    for (const image of slide?.querySelectorAll('img') ?? []) {
      if (image.sizes !== ZOOMED_SIZES) image.sizes = ZOOMED_SIZES;
    }
  };

  #renderViewer() {
    const viewer = this.#viewer;
    const band = this.#focusedId ? this.#band(this.#focusedId) : null;
    if (!viewer || !band) return;
    if (this.#addingId && this.#addingId !== band.id) this.#finishAdding();

    const ids = this.#viewerIds;
    const index = Math.max(0, ids.indexOf(band.id));
    const slide = this.#swiperElement?.children[index];
    const hasCombo = slide ? slide.hasAttribute('data-has-combo') : Boolean(band.composite);
    const added = this.#addedIds.includes(band.id);

    viewer.dataset.view = hasCombo ? this.#view : 'band';
    viewer.toggleAttribute('data-combo', hasCombo);

    const count = ids.length;
    const colors = ((count === 1 ? this.dataset.colorsOne : this.dataset.colorsOther) ?? '[count]').replace(
      '[count]',
      String(count)
    );
    this.#setText('[data-viewer-material]', `${band.materialLabel} · ${colors}`);
    this.#setText('[data-viewer-title]', band.title);
    this.#setText('[data-viewer-price]', this.#extra(band.price));
    this.#setText('[data-viewer-color]', band.color);
    this.#setText('[data-viewer-add-label]', (this.dataset.addLabel ?? '[price]').replace('[price]', this.#extra(band.price)));

    for (const button of this.querySelectorAll('[data-viewer-view]')) {
      if (button instanceof HTMLElement) {
        button.setAttribute('aria-pressed', String(button.dataset.viewerView === viewer.dataset.view));
      }
    }
    for (const button of this.querySelectorAll('[data-viewer-band]')) {
      if (!(button instanceof HTMLElement)) continue;
      button.setAttribute('aria-current', String(button.dataset.viewerBand === band.id));
      button.toggleAttribute('data-added', this.#addedIds.includes(button.dataset.viewerBand ?? ''));
    }

    const add = this.querySelector('[data-viewer-add]');
    add?.setAttribute('aria-pressed', String(added));
    if (this.#justAddedId !== band.id || !added) {
      this.#justAddedId = null;
      add?.removeAttribute('data-just-added');
    }
    if (add instanceof HTMLButtonElement) add.disabled = !this.#isAvailable(band.id);
    const remove = this.querySelector('[data-viewer-remove]');
    if (remove instanceof HTMLElement) remove.hidden = !added;
    const noPreview = this.querySelector('[data-viewer-no-preview]');
    if (noPreview instanceof HTMLElement) noPreview.hidden = added || hasCombo;

    const previous = this.querySelector('[data-viewer-prev]');
    const next = this.querySelector('[data-viewer-next]');
    if (previous instanceof HTMLButtonElement) previous.disabled = index === 0;
    if (next instanceof HTMLButtonElement) next.disabled = index >= count - 1;
  }

  /**
   * @param {string} selector
   * @param {string} text
   */
  #setText(selector, text) {
    const element = this.querySelector(selector);
    if (element && element.textContent !== text) element.textContent = text;
  }

  /**
   * @param {string} title
   * @param {boolean} added
   */
  #announce(title, added) {
    const status = this.querySelector('[ref="status"]');
    const template = added ? this.dataset.addedMessage : this.dataset.removedMessage;
    if (!status || !title || !template) return;
    status.textContent = template.replace('[title]', title);
  }
}

if (!customElements.get('buy-box-v2-bands')) {
  customElements.define('buy-box-v2-bands', BuyBoxV2Bands);
}
